import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  CardActions,
  List,
  ListItem,
  Divider,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Typography,
} from '@mui/material'
import { Box, IconButton, Button, ListItemText, Tooltip, Grid } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { show, codeTree } from '../../redux/slices/Drawer/drawerRight'
import { updateFeatures } from '../../redux/slices/Tree/treeFeatures'
import { getPopupDataByID, getTreePhotos } from '../../services'
import { getTreePhotoUrl } from '../../utils/imageUrl'
import {
  QrCode2Outlined,
  Cancel as CancelIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon,
  Edit,
  PhotoCamera,
  PhotoCameraOutlined,
  PlaceOutlined,
  BookmarkBorderOutlined,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material'
import CodeQrModal from '../../components/CodeQRModal'
import ContainerPopover from './ContainerPopover'
import ChipCustomTg from '../ChipCustomTg'
import TreePhotoGallery from '../TreePhotos/TreePhotoGallery'
import PopupSkeleton from './PopupSkeleton'
import { Header, Text, ButtonCustom } from './styles'

import './Popup.css'
import * as RT from '../../config/routeType'
import { DOMAIN_URL } from '../../actions/global'
import { detectCityFromURL } from '../../utils/cityDetector'
import { buildAbsoluteUrl, buildCantonUrl, buildTreePublicUrl } from '../../utils/cantonUrl'
import { useCities } from '../../context/CitiesContext'
import { setPopup, setPopupLoading, setShowAccessAlert, setShowTenantAlert } from '../../redux/slices/Map/map'
import { useTreePermissions } from '../../hooks/useTreePermissions'
import { getTreeTenantRef } from '../../utils/treePermissions'

const paddingInlineBody = 16
const getPhotoDisplayUrl = (photo, treeId, variant = 'md') =>
  photo?.variants?.[variant] || photo?.url || getTreePhotoUrl(treeId, photo?.filename)

const styleTextHeader = {
  textAlign: 'center',
  width: '100%',
  fontWeight: 750,
  fontSize: '15px',
  lineHeight: '16px',
  color: '#fff',
  paddingTop: 0.7,
  textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  position: 'relative',
  zIndex: 1,
}

const light = {
  green: { bg: '#25a52e', c: '#FFF' },
  yellow: { bg: '#EDD317', c: '#000' },
  red: { bg: '#D83020', c: '#FFF' },
  gray: { bg: '#4e37371b', c: '#504d4df5' },
}

const styleChip = [
  {
    id: 2,
    props: type => ({
      label: 'Enfermedad',
      badgeBackground: light?.[type]?.bg,
      badgeColor: light?.[type]?.c,
    }),
  },
  {
    id: 6,
    props: type => ({
      label: 'Problema físico',
      badgeBackground: light?.[type]?.bg,
      badgeColor: light?.[type]?.c,
    }),
  },
  {
    id: 7,
    props: type => ({
      label: 'Riesgos',
      badgeBackground: light?.[type]?.bg,
      badgeColor: light?.[type]?.c,
    }),
  },
  {
    id: 4,
    props: type => ({
      label: 'Plaga',
      badgeBackground: light?.[type]?.bg,
      badgeColor: light?.[type]?.c,
    }),
  },
  {
    id: 8,
    props: type => ({
      label: 'Recomendación',
      badgeBackground: light?.[type]?.bg,
      badgeColor: light?.[type]?.c,
    }),
  },
]

export default function PopupContent({
  tree,
  closePopup,
  closePopupWithNavigation,
  showDescriptionBotton,
  isPinned,
  onTogglePin,
}) {
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { getEnabledCantones } = useCities()
  const cantones = getEnabledCantones()
  const [showQR, setShowQR] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false)
  const [confirmEditOpen, setConfirmEditOpen] = useState(false)
  const [dialogPhotos, setDialogPhotos] = useState([])
  const [dialogPhotoIndex, setDialogPhotoIndex] = useState(0)
  const [dialogPhotosLoading, setDialogPhotosLoading] = useState(false)
  const [fetchError, setFetchError] = useState(null)
  const [retryNonce, setRetryNonce] = useState(0)
  const requestIdRef = useRef(0)
  const _treeState = useSelector(state => state.treeFeatures)
  const { features: data } = _treeState.data || {}
  const popupLoading = useSelector(state => state.mapState.popup.loading)

  const treeId = tree?.properties.id || null
  const code = tree?.properties.code || null

  const isOpenDrawerRight = useSelector(state => state.drawerRight.value)
  const authenticated = useSelector(state => state.login.authenticated)
  const { auth } = authenticated || {}
  const { canViewTreeDetail, canUpdateTree, canUpdateAnyTenant } = useTreePermissions()

  const treeFeature = useMemo(
    () => (data || []).find(obj => String(obj.properties?.id) === String(treeId)),
    [data, treeId]
  )
  // tenantId llega con los datos del popup (Redux); el GeoJSON solo trae tenantSlug, que puede ir atrasado
  const treeTenantRef = getTreeTenantRef(treeFeature?.properties, tree?.properties)

  const treeAttributes = treeFeature?.properties?.attributes || []
  const inconveniences = treeFeature?.properties?.inconveniences || []
  const scientificName = (treeFeature?.properties?.specie || {}).name || ''
  const classifier = (treeFeature?.properties?.specie || {}).classifier || ''
  const pseudonymName =
    ((treeFeature?.properties?.specie || {}).pseudonym || [])[0]?.name || ''

  const specie = treeFeature?.properties?.specie || {}
  const site = treeFeature?.properties?.site || {}
  const locationReference = treeFeature?.properties?.locationReference || null

  const wordsLength = `${scientificName + classifier}`.length > 36
  const drawerHeight = wordsLength ? '5em' : '3.8em'

  let featuresBasic = {
    code: { label: 'Código', value: code || 'No encontrada' },
    family: { label: 'Familia', value: specie?.family?.name || 'No encontrada' },
    site: { label: 'Sitio', value: site.name || 'No encontrada' },
    reference: { label: 'Referencia', value: locationReference || 'Sin referencia' },
  }

  const keysToRender = showDescriptionBotton
    ? ['site', 'reference', 'code']
    : ['family', 'site']

  const fontFamilyType = theme.typography.fontFamily

  const getIndexOfFeature = useCallback((id) => {
    let idFeatureState = -1
    const normalizeId = value => (value == null ? '' : String(value))
    data.forEach((obj, key) => {
      if (normalizeId(obj.properties?.id) === normalizeId(id)) idFeatureState = key
    })
    return idFeatureState
  }, [data])

  function chipForDiseases(obj) {
    const length = obj.attributes.length
    const type = length > 5 ? 'red' : length > 3 ? 'yellow' : 'green'
    const content = length
    return { type, content }
  }

  function chipForPests(obj) {
    const length = obj.attributes.length
    const type = length > 5 ? 'red' : length > 3 ? 'yellow' : 'green'
    const content = length
    return { type, content }
  }

  function getValueForMetaFid(treeAttributes, targetMetaFid) {
    const item = treeAttributes.find(attr => attr?.metaFid === targetMetaFid)
    return item ? item.value : null
  }

  function calcularNivelDeRiesgo(obj, straightnessValor) {
    if (!Boolean(straightnessValor)) return {}

    let countRisk = 0
    let straightness = 1
    let derriboValida = false
    let rishValue = 0
    let numRiskTotal = 0
    let diseaseIssues = 0
    let pestIssues = 0

    // Mapeo de rectitud
    const weightStraightness = {
      recto: 1,
      torcido: 1.05,
      inclinado: 1.45,
      muyinclinado: 1.5,
    }

    // Grupos de riesgo con IDs
    const riskGroups = [
      { ids: [62, 63, 64], weight: 0.7 }, // Caída
      { ids: [65, 66, 67], weight: 0.1 }, // Afección
      { ids: [68, 69, 70], weight: 0.05 }, // Interferencia circulación
      { ids: [71, 72, 73], weight: 0.05 }, // Infraestructura
      { ids: [74, 75, 76], weight: 0.1 }, // Redes eléctricas
    ]
    // Niveles numéricos por valor textual (puedes ajustar esto según tu escala)
    const riskLevelMap = { bajo: 1, medio: 2, alto: 3 }
    const diseaseSet = new Set([22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 86])
    const pestSet = new Set([39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 88])

    // Obtener IDs y valores de riesgo
    const inconveniences = obj.attributes || []
    if (!Boolean(inconveniences?.length)) return {}

    for (const inc of inconveniences) {
      const key = Object.keys(inc)[0] // e.g. 'rsgoCaida'
      const data = inc[key] // e.g. { id: 63, value: 'Bajo' }
      const id = data.id
      if (data?.id === 83) derriboValida = true

      const valor = data.value?.toLowerCase().replace(/\s+/g, '') // "bajo", "medio", ...

      if (diseaseSet.has(id)) diseaseIssues++
      if (pestSet.has(id)) pestIssues++

      if (riskLevelMap[valor] !== undefined)
        for (const group of riskGroups)
          if (group.ids.includes(id)) {
            countRisk += riskLevelMap[valor] * group.weight
            numRiskTotal++
            break
          }
    }

    // Rectitud transformada
    const rectitudKey = straightnessValor.toLowerCase().replace(/\s+/g, '')
    straightness = weightStraightness[rectitudKey]

    if (numRiskTotal === 5 && straightness !== undefined) {
      rishValue = countRisk * straightness
      if (derriboValida) rishValue += 0.5
      if (diseaseIssues > 5) rishValue += 0.5
      if (pestIssues > 5) rishValue += 0.5
    }

    const level = rishValue < 1.6 ? 'Bajo' : rishValue < 2.3 ? 'Medio' : 'Alto'

    return {
      level,
      rishValue: rishValue.toFixed(2),
      straightness,
      countRisk: countRisk.toFixed(2),
    }
  }

  function chipForPotentialRisks(obj) {
    const valueFusteRectitud = getValueForMetaFid(treeAttributes, 14)
    const risk = calcularNivelDeRiesgo(obj, valueFusteRectitud)

    if (!Object.keys(risk).length) return { type: null, content: null }

    const type =
      risk.level === 'Bajo' ? 'green' : risk.level === 'Medio' ? 'yellow' : 'red'
    return { type, content: risk.level.charAt(0) }
  }

  function chipForPhysicalProblems(obj) {
    const length = obj.attributes.length
    const type = length > 4 ? 'red' : length > 2 ? 'yellow' : 'green'
    const content = length
    return { type, content }
  }
  function chipForSuggestions(obj) {
    const length = obj.attributes.length
    const type = length > 5 ? 'red' : length > 3 ? 'yellow' : 'green'
    const content = length
    return { type, content }
  }

  const chipOptions = {
    2: obj => chipForDiseases(obj),
    4: obj => chipForPests(obj),
    6: obj => chipForPhysicalProblems(obj),
    7: obj => chipForPotentialRisks(obj),
    8: obj => chipForSuggestions(obj),
  }

  const handleCodeQr = () => setShowQR(true)
  const handleCloseAction = () => setShowQR(false)
  const handleActiveDrawerRight = () => {
    if (!canViewTreeDetail(treeTenantRef)) {
      dispatch(setShowAccessAlert(true))
      return
    }
    dispatch(show(true))
    dispatch(codeTree(getIndexOfFeature(treeId)))
  }

  const currentCity = detectCityFromURL(location.pathname) || 'loja'
  const qrData = code
    ? buildAbsoluteUrl(buildTreePublicUrl(currentCity, cantones, code), DOMAIN_URL)
    : ''

  // Lógica para mapa en modo editar-arbol
  const { treeId: urlTreeId } = useParams()
  const isOnEditRoute = location.pathname.includes(RT.MANAGEMENT_TREE)
  const isOtherTree = isOnEditRoute && String(treeId) !== String(urlTreeId)

  const handleClosePopup = () => {
    requestIdRef.current += 1
    if (isOnEditRoute) {
      // En editar-arbol: el container decide si minimizar o cerrar; nunca navegar aquí
      closePopup()
      return
    }
    if (isOpenDrawerRight) {
      closePopup()
    } else {
      closePopup()
      if (!location.pathname.includes(RT.REGISTER_TREE)) {
        navigate(buildCantonUrl(currentCity, cantones), { replace: true })
      }
    }
  }

  const handleEdit = () => {
    if (!canUpdateTree(treeTenantRef)) {
      dispatch(setShowTenantAlert(true))
      return
    }
    dispatch(show(false))
    navigate(buildCantonUrl(currentCity, cantones, `/${RT.MANAGEMENT_TREE}/${treeId}`))
  }

  useEffect(() => {
    if (!treeFeature) return

    const currentDataType = treeFeature?.properties?._dataType
    if (currentDataType === 'full' || currentDataType === 'popup') {
      dispatch(setPopup({ open: true, code, id: treeId }))
      dispatch(setPopupLoading(false))
      return
    }

    const requestId = ++requestIdRef.current
    setFetchError(null)
    dispatch(setPopupLoading(true))

    getPopupDataByID(treeId)
      .then(propertiesByID => {
        if (requestId !== requestIdRef.current) return
        dispatch(updateFeatures({ someId: getIndexOfFeature(treeId), someValue: propertiesByID }))
        dispatch(setPopup({ open: true, code, id: treeId }))
      })
      .catch(err => {
        if (requestId !== requestIdRef.current) return
        console.error('Error al obtener propiedades popup:', err.message)
        setFetchError(err.message || 'Error al cargar los datos del árbol')
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return
        dispatch(setPopupLoading(false))
      })

    return () => { requestIdRef.current += 1 }
  }, [treeFeature, treeId, code, dispatch, getIndexOfFeature, retryNonce])

  useEffect(() => {
    if (!photoDialogOpen || !treeId) return
    setDialogPhotosLoading(true)
    setDialogPhotoIndex(0)
    getTreePhotos(treeId)
      .then(data => setDialogPhotos(Array.isArray(data) ? data : []))
      .catch(() => setDialogPhotos([]))
      .finally(() => setDialogPhotosLoading(false))
  }, [photoDialogOpen, treeId])

  if (fetchError) {
    return (
      <Box
        sx={{
          minWidth: showDescriptionBotton ? '18em' : '25em',
          maxWidth: showDescriptionBotton ? '24em' : 'none',
          backgroundColor: 'background.paper',
        }}>
        <Header style={{ minHeight: '3.8em', paddingLeft: 4 }}>
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            onClick={handleClosePopup}
            sx={{ width: '30px', px: 1, paddingRight: '10px', paddingBottom: '5px', marginRight: 0.5 }}>
            <CancelIcon sx={{ fill: '#a8f446', width: 20, height: 16 }} />
          </IconButton>
        </Header>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="error" sx={{ mb: 1.5, fontFamily: fontFamilyType }}>
            No se pudo cargar la información del árbol.
          </Typography>
          <Button size="small" variant="outlined" onClick={() => setRetryNonce(n => n + 1)}>
            Reintentar
          </Button>
        </Box>
      </Box>
    )
  }

  const currentDataType = treeFeature?.properties?._dataType
  const hasLoadedPopupData = currentDataType === 'popup' || currentDataType === 'full'

  if (popupLoading || !hasLoadedPopupData) {
    return <PopupSkeleton showDescriptionBotton={showDescriptionBotton} onClose={handleClosePopup} />
  }

  return (
    <Box
      sx={{
        minWidth: showDescriptionBotton ? '18em' : '25em',
        maxWidth: showDescriptionBotton ? '24em' : 'none',
        boxShadow: 'none',
        backgroundColor: 'background.paper',
      }}>
      <Header style={{ minHeight: drawerHeight, paddingLeft: 4 }}>
        {onTogglePin !== undefined && (
          <IconButton
            aria-label={isPinned ? 'desanclar popup' : 'anclar popup'}
            onClick={onTogglePin}
            size="small"
            sx={{
              width: '30px',
              px: 1,
              paddingLeft: '10px',
              paddingBottom: '5px',
              marginLeft: 0.5,
              flexShrink: 0,
            }}>
            {isPinned ? (
              <PushPinIcon sx={{ fill: '#a8f446', width: 18, height: 18 }} />
            ) : (
              <PushPinOutlinedIcon
                sx={{ fill: 'rgba(168,244,70,0.55)', width: 18, height: 18 }}
              />
            )}
          </IconButton>
        )}
        <Text sx={styleTextHeader}>
          {pseudonymName && (
            <Box pb={0.3}>
              <span style={{ textTransform: 'uppercase', color: '#fcf7f0ff' }}>
                {pseudonymName}
              </span>
              <br />
            </Box>
          )}
          <span
            style={{
              fontStyle: 'italic',
              fontWeight: 550,
              paddingBottom: 1,
              fontSize: '14px',
            }}>
            {scientificName}
          </span>
          {wordsLength ? <br /> : null}
          <span style={{ fontSize: 12, color: '#ffc107' }}>
            {Boolean(classifier) ? ` ${classifier}` : ''}
          </span>
        </Text>
        <IconButton
          aria-label="close"
          onClick={handleClosePopup}
          size="small"
          sx={{
            width: '30px',
            px: 1,
            paddingRight: '10px',
            paddingBottom: '5px',
            marginRight: 0.5,
          }}>
          <CancelIcon sx={{ fill: '#a8f446', width: 20, height: 16 }} />
        </IconButton>
      </Header>
      {!showQR ? (
        <div
          style={{
            paddingInline: showDescriptionBotton ? 12 : paddingInlineBody,
            paddingTop: showDescriptionBotton ? 0 : 6,
          }}>
          {!showDescriptionBotton && (
            <ContainerPopover
              attributes={treeFeature?.properties?.attributes}
              carbon={treeFeature?.properties?.carbon}
            />
          )}
          {showDescriptionBotton ? (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="fullWidth"
                  sx={{
                    minHeight: 36,
                    '& .MuiTab-root': {
                      minWidth: 0,
                      flex: 1,
                      minHeight: 36,
                      py: 0.5,
                      px: 0,
                    },
                  }}>
                  <Tab
                    icon={
                      <Tooltip title="Código" placement="top" arrow>
                        <QrCode2Outlined sx={{ fontSize: 18 }} />
                      </Tooltip>
                    }
                  />
                  <Tab
                    icon={
                      <Tooltip title="Sitio" placement="top" arrow>
                        <PlaceOutlined sx={{ fontSize: 18 }} />
                      </Tooltip>
                    }
                  />
                  <Tab
                    icon={
                      <Tooltip title="Referencia" placement="top" arrow>
                        <BookmarkBorderOutlined sx={{ fontSize: 18 }} />
                      </Tooltip>
                    }
                  />
                  <Tab
                    icon={
                      <Tooltip title="Foto" placement="top" arrow>
                        <PhotoCameraOutlined sx={{ fontSize: 18 }} />
                      </Tooltip>
                    }
                  />
                </Tabs>
              </Box>

              <Box sx={{ pt: 0.25, pb: 0.25, textAlign: 'center' }}>
                {activeTab < 3 && (
                  <Box>
                    <Box
                      sx={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: 'text.secondary',
                        mb: 0.25,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                      {featuresBasic[['code', 'site', 'reference'][activeTab]].label}
                    </Box>
                    <Box
                      sx={{
                        fontSize: '13px',
                        fontWeight: 550,
                        color: 'text.primary',
                        wordBreak: 'break-word',
                        lineHeight: 1.3,
                      }}>
                      {featuresBasic[['code', 'site', 'reference'][activeTab]].value}
                    </Box>
                  </Box>
                )}
                {activeTab === 3 && (
                  <Box
                    sx={{
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                    }}>
                    <TreePhotoGallery
                      treeId={treeId}
                      compact
                      onImageClick={() => setPhotoDialogOpen(true)}
                    />
                  </Box>
                )}
              </Box>

              {/* Botón: cargar este árbol en el formulario (árbol diferente al del formulario) */}
              {isOtherTree && (
                <Box sx={{ pt: 0.2, pb: 0.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    color="warning"
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      lineHeight: 1.1,
                    }}
                    onClick={() => {
                      if (!canUpdateTree(treeTenantRef)) { dispatch(setShowTenantAlert(true)); return }
                      setConfirmEditOpen(true)
                    }}>
                    Editar este árbol
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <List sx={{ py: 0 }}>
              {keysToRender.map(key => (
                <ListItem
                  key={`feature-${key}`}
                  component="div"
                  disablePadding
                  sx={{
                    minHeight: 18,
                    justifyContent: 'initial',
                    py: 0,
                  }}>
                  <ListItemText sx={{ marginTop: '2px', px: 1 }}>
                    <Grid
                      container
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontStyle: 'normal',
                        fontSize: '13px',
                        lineHeight: '12px',
                        textAlign: 'center',
                        color: theme.palette.text.primary,
                      }}>
                      <Grid item xs={3} alignContent="center" fontWeight="600">
                        {featuresBasic[`${key}`].label}:
                      </Grid>
                      <Grid
                        item
                        xs={9}
                        alignContent="center"
                        fontWeight="500"
                        sx={{
                          borderRadius: '10px',
                          border: '2px solid rgba(0, 0, 0, 0.04)',
                          backgroundColor: '#84a93e16',
                          py: 0.3,
                        }}>
                        {featuresBasic[`${key}`].value}
                      </Grid>
                    </Grid>
                  </ListItemText>
                </ListItem>
              ))}
            </List>
          )}

          {!showDescriptionBotton && (
            <Divider sx={{ mt: 0.2, bgcolor: 'primary.main' }} />
          )}

          {!showDescriptionBotton && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexFlow: 'row wrap',
                  justifyContent: 'space-evenly',
                  gap: '8px',
                  pt: 1,
                }}>
                {styleChip.map((style, i) => {
                  if (style.id === 8 && !Boolean(auth)) return null
                  const inconvenience = inconveniences.find(obj => obj.id === style.id)
                  const res = inconvenience
                    ? chipOptions[style.id]?.(inconvenience)
                    : { content: '0', type: 'gray' }

                  return (
                    <Box
                      key={`Chip-${i}`}
                      sx={{
                        transition: 'transform 0.2s ease',
                        '&:hover': { transform: 'translateY(-2px)' },
                      }}>
                      <ChipCustomTg
                        badgeContent={res?.content}
                        {...style.props(res?.type)}
                      />
                    </Box>
                  )
                })}
              </Box>
              <CardActions
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pt: 1,
                  px: 0,
                  m: 0,
                  '& > *': { flex: 1, display: 'flex', alignItems: 'center' },
                  '& > :not(:first-of-type)': { marginLeft: 0 },
                }}>
                <Box sx={{ justifyContent: 'flex-start' }}>
                  <Tooltip
                    title="Ver código QR"
                    followCursor
                    enterDelay={50}
                    leaveDelay={100}>
                    <IconButton
                      aria-label="Ver código QR"
                      onClick={handleCodeQr}
                      sx={{ p: 0, color: theme.palette.primary.main }}>
                      <QrCode2Outlined sx={{ fontSize: 26 }} />
                    </IconButton>
                  </Tooltip>
                  <Box
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      lineHeight: 1.1,
                      color: theme.palette.text.forestGreen,
                      ml: 0.5,
                    }}>
                    {(() => {
                      if (!code) return null
                      if (code.includes('+')) {
                        return code.split('+').map((part, i) => (
                          <span key={i}>
                            {i > 0 && (
                              <>
                                <br />+
                              </>
                            )}
                            {part}
                          </span>
                        ))
                      }
                      const m = code.match(/^(.*?)(\.S\d+.*)$/)
                      if (m)
                        return (
                          <>
                            <span>{m[1]}</span>
                            <br />
                            <span>{m[2]}</span>
                          </>
                        )
                      return code
                    })()}
                  </Box>
                </Box>

                <Box sx={{ justifyContent: 'center' }}>
                  <ButtonCustom
                    onClick={handleActiveDrawerRight}
                    variant="contained"
                    size="small"
                    sx={{
                      height: 27,
                      width: 84,
                      fontFamily: fontFamilyType,
                      fontSize: 14,
                      fontWeight: 550,
                    }}>
                    {`Ver más`}
                  </ButtonCustom>
                </Box>

                <Box sx={{ justifyContent: 'space-around' }}>
                  {canUpdateAnyTenant && (
                    <Tooltip title="Editar" followCursor enterDelay={50} leaveDelay={100}>
                      <IconButton
                        color="primary"
                        aria-label="Editar"
                        onClick={handleEdit}
                        size="small">
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip
                    title="Ver fotos del árbol"
                    followCursor
                    enterDelay={50}
                    leaveDelay={100}>
                    <IconButton
                      onClick={() => setPhotoDialogOpen(true)}
                      color="primary"
                      size="small">
                      <PhotoCamera />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardActions>
            </>
          )}
        </div>
      ) : (
        <CodeQrModal
          onClose={handleCloseAction}
          treeId={code}
          data={qrData}
          isAuth={auth}
        />
      )}

      {/* Confirmación: cambiar árbol en edición */}
      <Dialog open={confirmEditOpen} onClose={() => setConfirmEditOpen(false)}>
        <DialogTitle
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: 15,
            fontWeight: 700,
            pb: 0.5,
            color: theme.palette.primary.main,
          }}>
          ¿Cambiar árbol en edición?
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ fontFamily: theme.typography.fontFamily, fontSize: 13 }}>
            Los cambios no guardados se perderán.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button size="small" onClick={() => setConfirmEditOpen(false)}>
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            onClick={() => {
              setConfirmEditOpen(false)
              closePopupWithNavigation()
              navigate(buildCantonUrl(currentCity, cantones, `/${RT.MANAGEMENT_TREE}/${treeId}`))
            }}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        maxWidth="md"
        fullWidth>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            pt: 1.5,
            pb: 1,
          }}>
          <Box
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: '16px',
              fontWeight: 700,
              color: 'text.primary',
            }}>
            Fotos del árbol
            {dialogPhotos.length > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  fontSize: '13px',
                  fontWeight: 400,
                  color: 'text.secondary',
                }}>
                {dialogPhotoIndex + 1} / {dialogPhotos.length}
              </Box>
            )}
          </Box>
          <IconButton size="small" onClick={() => setPhotoDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{ p: 0, bgcolor: '#111', position: 'relative', minHeight: 280 }}>
          {dialogPhotosLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 280,
              }}>
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          ) : dialogPhotos.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 280,
              }}>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontFamily: theme.typography.fontFamily,
                }}>
                Sin fotos registradas
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                component="img"
                src={getPhotoDisplayUrl(dialogPhotos[dialogPhotoIndex], treeId, 'md')}
                alt={dialogPhotos[dialogPhotoIndex]?.originalName || 'Foto del árbol'}
                sx={{
                  width: '100%',
                  maxHeight: '75vh',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
              {dialogPhotos.length > 1 && (
                <>
                  <IconButton
                    onClick={() =>
                      setDialogPhotoIndex(
                        i => (i - 1 + dialogPhotos.length) % dialogPhotos.length,
                      )
                    }
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0,0,0,0.45)',
                      color: 'common.white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    }}>
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      setDialogPhotoIndex(i => (i + 1) % dialogPhotos.length)
                    }
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0,0,0,0.45)',
                      color: 'common.white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    }}>
                    <ChevronRightIcon />
                  </IconButton>
                </>
              )}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  px: 2,
                  py: 0.75,
                  background:
                    'linear-gradient(0deg, rgba(0,0,0,0.65) 0%, transparent 100%)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.85)',
                    fontFamily: theme.typography.fontFamily,
                  }}>
                  {dialogPhotos[dialogPhotoIndex]?.originalName ||
                    `Foto ${dialogPhotoIndex + 1}`}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    fontFamily: theme.typography.fontFamily,
                  }}>
                  {((dialogPhotos[dialogPhotoIndex]?.size || 0) / 1024).toFixed(0)} KB
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

    </Box>
  )
}
